import React from "react";

import { createTimestamp, serializeTimestamp } from "@/shared/text";

import { HoverActionToolbar, hoverActions } from "./HoverActionToolbar";
import { pickDisplayLabelNode } from "./pickDisplayLabel";
import { useJumpToTimestampAction, useSetAnchorAction } from "./timeFormat";
import styles from "./Timestamp.module.css";

/**
 * A wall-clock instant in markdown, whichever way it was written: the AI's
 * `t=<n>ns` mentions (which AgentThread rewrites to its `ai-ts:` scheme) and
 * `[label](timestamp:<ns>)` links, which older comments carry.
 *
 * The display text comes from {@link pickDisplayLabelNode}: a custom label
 * (e.g. `[start of segment](timestamp:…)`) is kept verbatim; a missing or
 * numeric-looking label is replaced with the user's TimestampFormat. `title`
 * carries the raw ns, so a reader can recover the exact number.
 *
 * Its actions live in the hover toolbar: "Jump to timestamp" while a
 * visualizer is open, and "Set as t=0" wherever a
 * {@link TimestampFormatProvider} is in reach to hold the anchor. Clicking the
 * text itself does nothing. The text is link-coloured whenever there is an
 * action to offer, and underlined while the toolbar is open.
 *
 * A mention the AI wrote in backticks keeps its inline-code styling.
 *
 * It is an `<a href="timestamp:…">`, so copying it yields a
 * `[label](timestamp:…)` link to the same instant, labelled with the displayed
 * text. A backticked mention copied together with surrounding text is the
 * exception: its `<code>` serializes as `` `label` ``, dropping the link.
 */
export const Timestamp: React.FC<{
  ns: bigint;
  /** The markdown link's label; omitted for a bare `t=<n>ns` mention. */
  children?: React.ReactNode;
  /** Wraps the timestamp in `<code>`, for a mention written in backticks. */
  highlighted?: boolean;
}> = ({ ns, children, highlighted = false }) => {
  const actions = hoverActions(
    useJumpToTimestampAction(ns),
    useSetAnchorAction(ns),
  );

  const link = (
    <a
      href={serializeTimestamp(createTimestamp(ns))}
      className={actions.length > 0 ? styles.actionable : styles.text}
      tabIndex={-1}
      onClick={(event) => {
        // The browser has no handler for the `timestamp:` scheme.
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      {pickDisplayLabelNode(children, ns)}
    </a>
  );

  return (
    <HoverActionToolbar actions={actions} className={styles.instant}>
      {/* The `title` span wraps the link because copying the timestamp alone
          serializes the label's parent element, which must be the `<a>`, and
          a `title` on the `<a>` would be copied as a markdown link title. */}
      <span title={`${ns} ns`}>{highlighted ? <code>{link}</code> : link}</span>
    </HoverActionToolbar>
  );
};
